import { useMemo } from 'react';
import type { API } from '@/api';
import {
  MAX_RESERVATION_TIME_LENGTH_IN_MINUTES,
  RESERVABLE_TIME_UNIT_IN_MINUTES,
  SPACE_TYPE,
} from '@/constants/space';

type TimeSlot =
  API['GET']['PrivateRoomAvailableTimeSlots']['response']['spaces'][number]['time_slots'][number];

const TIME_UNIT = RESERVABLE_TIME_UNIT_IN_MINUTES[SPACE_TYPE.PRIVATE_ROOM];
const MAX_MINUTES =
  MAX_RESERVATION_TIME_LENGTH_IN_MINUTES[SPACE_TYPE.PRIVATE_ROOM];

/**
 * 防音個室ブースの利用可能な時間・分・期間を計算するhook
 * @param timeSlots 時間スロットの配列
 * @param selectedHour 選択された時間（"HH"形式）
 * @param selectedStartTime 選択された開始時刻（"HH:MM"形式）
 * @param remainingMinutes 残り利用可能時間（分）。undefinedの場合は最大時間
 */
export const usePrivateRoomAvailability = (
  timeSlots: TimeSlot[],
  selectedHour?: string,
  selectedStartTime?: string,
  remainingMinutes?: number,
) => {
  // 利用可能な時間（hour）のマップ
  const availableHoursMap = useMemo(() => {
    const hourMap = new Map<string, boolean>();

    timeSlots.forEach((slot) => {
      const [hour] = slot.time.split(':');
      const isSlotAvailable = slot.available && slot.displayable;

      // 既に利用可能と判定されているか、新しいスロットが利用可能な場合にtrueを設定
      if (hourMap.get(hour) || isSlotAvailable) {
        hourMap.set(hour, true);
      } else if (!hourMap.has(hour)) {
        hourMap.set(hour, false);
      }
    });

    return hourMap;
  }, [timeSlots]);

  // すべての時間選択肢（05～22時）
  const allHours = [
    '05',
    '06',
    '07',
    '08',
    '09',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '20',
    '21',
    '22',
  ];

  // 利用可能な分（minute）のセット
  const availableMinutesSet = useMemo(() => {
    if (!selectedHour) return new Set<string>();

    const minuteSet = new Set<string>();
    timeSlots
      .filter(
        (slot) =>
          slot.time.startsWith(`${selectedHour}:`) &&
          slot.available &&
          slot.displayable,
      )
      .forEach((slot) => {
        minuteSet.add(slot.time.split(':')[1]);
      });
    return minuteSet;
  }, [timeSlots, selectedHour]);

  // すべての分選択肢（00, 05, 10, ..., 55）
  const allMinutes = [
    '00',
    '05',
    '10',
    '15',
    '20',
    '25',
    '30',
    '35',
    '40',
    '45',
    '50',
    '55',
  ];

  // 残り時間を考慮した最大利用時間
  const effectiveMaxDuration = useMemo(() => {
    if (remainingMinutes === undefined) return MAX_MINUTES;
    return Math.min(remainingMinutes, MAX_MINUTES);
  }, [remainingMinutes]);

  // 利用可能な期間（duration）のセット
  const availableDurationsSet = useMemo(() => {
    if (!selectedStartTime) return new Set<number>();

    // 開始時刻のインデックスを取得
    const startIndex = timeSlots.findIndex(
      (slot) => slot.time === selectedStartTime,
    );
    if (startIndex === -1) return new Set<number>();

    const durations = new Set<number>();

    // TIME_UNIT分刻みで残り時間まで、連続して利用可能かチェック
    for (
      let duration = TIME_UNIT;
      duration <= effectiveMaxDuration;
      duration += TIME_UNIT
    ) {
      const slotsNeeded = duration / TIME_UNIT; // 必要なスロット数
      let allAvailable = true;

      // 開始時刻から必要なスロット数分、すべて利用可能かチェック
      for (let i = 0; i < slotsNeeded; i++) {
        const slotIndex = startIndex + i;
        if (
          slotIndex >= timeSlots.length ||
          !timeSlots[slotIndex].available ||
          !timeSlots[slotIndex].displayable
        ) {
          allAvailable = false;
          break;
        }
      }

      if (allAvailable) {
        durations.add(duration);
      } else {
        // 連続していない場合は、それ以降のdurationも選択不可
        break;
      }
    }

    return durations;
  }, [timeSlots, selectedStartTime, effectiveMaxDuration]);

  // すべての利用時間選択肢（TIME_UNIT～MAX_MINUTES、TIME_UNIT分刻み）
  // 選択可否はavailableDurationsSetで判定
  const allDurations = useMemo(() => {
    const durations: number[] = [];
    for (let d = TIME_UNIT; d <= MAX_MINUTES; d += TIME_UNIT) {
      durations.push(d);
    }
    return durations;
  }, []);

  return {
    availableHoursMap,
    allHours,
    availableMinutesSet,
    allMinutes,
    availableDurationsSet,
    allDurations,
  };
};
